/**
 * Where to put the user back after they finish phase 2 of onboarding.
 *
 * A `browse`-tier user who clicks a bid, an order row, or "Place new order" is
 * interrupted and sent into the funnel. This records what they were doing so
 * the end of phase 2 returns them to it rather than dumping them on the
 * dashboard.
 *
 * `sessionStorage`, not `localStorage`: it is a within-session breadcrumb, and
 * it must not survive into a different tab or a later visit where the intent no
 * longer holds. It is a convenience only — every consumer must work when it is
 * absent (private mode, storage disabled, a fresh tab).
 */
const RETURN_KEY = 'onboarding_return_to'

/** Same-origin, single-leading-slash paths only. */
const isSafeInternalPath = (path: string): boolean =>
  path.startsWith('/') && !path.startsWith('//') && !path.includes('://')

export const setOnboardingReturnTo = (path: string): void => {
  try {
    if (isSafeInternalPath(path)) sessionStorage.setItem(RETURN_KEY, path)
  } catch {
    // Storage unavailable (private mode, blocked cookies). The return path is a
    // nicety; losing it must never break the funnel.
  }
}

/**
 * Read and CLEAR the stored path.
 *
 * Consuming it on read means a later, unrelated completion cannot be hijacked
 * by a stale breadcrumb. Returns `null` when there is nothing safe to use —
 * 🔴 never trust the stored string: it is attacker-writable in principle, so it
 * is re-validated here and not only on write.
 */
export const takeOnboardingReturnTo = (): string | null => {
  try {
    const stored = sessionStorage.getItem(RETURN_KEY)
    sessionStorage.removeItem(RETURN_KEY)
    if (stored && isSafeInternalPath(stored)) return stored
    return null
  } catch {
    return null
  }
}
